import React, { useCallback, useRef, useEffect } from 'react'
import { arrayOf, oneOf, string, bool } from 'prop-types'
import resolveLinkProps from '../../resolveLinkProps'
import { useRouter } from 'next/router'
import Link from 'next/link'
import { Button } from '@corratech/pylot-ui'

const ButtonItem = (props) => {
    const {
        buttonType,
        link,
        linkType,
        openInNewTab = false,
        text,
        textAlign,
        border,
        borderColor,
        borderWidth,
        borderRadius,
        marginTop,
        marginRight,
        marginBottom,
        marginLeft,
        paddingTop,
        paddingRight,
        paddingBottom,
        paddingLeft,
        cssClasses = []
    } = props

    const dynamicInnerStyles = {
        textAlign,
        border,
        borderColor,
        borderWidth,
        borderRadius,
        marginTop,
        marginRight,
        marginBottom,
        marginLeft,
        paddingTop,
        paddingRight,
        paddingBottom,
        paddingLeft
    }

    const history = useRouter()
    let linkProps = {}
    let url = ''

    if (typeof link === 'string') {
        linkProps = resolveLinkProps(link, linkType)
        url = (linkProps.to ? linkProps.to : linkProps.href).trim()
    }

    const handleClick = useCallback(() => {
        if (!url) return

        if (openInNewTab) {
            window.open(url, '_blank')
        } else if (linkProps.to) {
            history.push(url)
        } else {
            window.location.assign(url)
        }
    }, [openInNewTab, url, linkProps.to, history])

    const parentRef = useRef(null)

    useEffect(() => {
        const parent = parentRef.current
        if (!parent) return

        const parentClasses = [...parent.classList]
        const homePageDynamicClass = parentClasses.find((cls) =>
            cls.startsWith('homeSection-')
        )
        const bannerHeroDynamicClass = parentClasses.find((cls) =>
            cls.startsWith('homeAction-')
        )

        const sectionName = homePageDynamicClass
            ? homePageDynamicClass.replace('homeSection-', '')
            : null
        const clickName = bannerHeroDynamicClass
            ? bannerHeroDynamicClass.replace('homeAction-', '')
            : null

        const interactive = parent.querySelector('button, a')
        if (!interactive) return

        if (sectionName) {
            interactive.setAttribute('data-click-section', sectionName)
        }
        if (clickName) {
            interactive.setAttribute('data-click-name', clickName)
        }
    }, [])

    const label = cssClasses.includes('home-duo-cta') ? (
        <span>{text}</span>
    ) : (
        text
    )

    const renderButton = () => {
        if (buttonType === 'link') {
            if (linkProps.to) {
                return (
                    <Link
                        type="link"
                        className="links"
                        {...linkProps}
                        {...(openInNewTab ? { target: '_blank' } : {})}
                        style={dynamicInnerStyles}
                    >
                        {label}
                    </Link>
                )
            }

            return (
                <a
                    {...linkProps}
                    {...(openInNewTab ? { target: '_blank' } : {})}
                >
                    {label}
                </a>
            )
        }

        return (
            <Button variant={buttonType} onClick={handleClick}>
                {label}
            </Button>
        )
    }

    return (
        <div
            ref={parentRef}
            className={cssClasses.length ? cssClasses.join(' ') : undefined}
        >
            {renderButton()}
        </div>
    )
}

ButtonItem.propTypes = {
    buttonType: oneOf(['primary', 'secondary', 'link']),
    link: string,
    linkType: oneOf(['default', 'category', 'product', 'page']),
    openInNewTab: bool,
    text: string,
    textAlign: string,
    border: string,
    borderColor: string,
    borderWidth: string,
    borderRadius: string,
    marginTop: string,
    marginRight: string,
    marginBottom: string,
    marginLeft: string,
    paddingTop: string,
    paddingRight: string,
    paddingBottom: string,
    paddingLeft: string,
    cssClasses: arrayOf(string)
}

export default ButtonItem
